// The live gantt's api handle, so the right panel can edit what the chart has selected
// (TODO §26/§8).
//
// Much smaller than `diagramEditing.tsx`, and deliberately: a gantt is **always live** in an
// editable window (§26/§2), so there is no one-instance rule to enforce and no click-into
// state machine. What has to cross the tree is "which gantt is the user in, and what is its
// api" — the panel cannot reach into a component otherwise.
//
// §26/§8.6 It does model **mounted vs selected**, which it did not at first. See `register`
// and `claim` below: the difference is invisible while the panel is the only consumer and
// load-bearing the moment anything asks "is there a gantt to edit right now?".
//
// Provided ONLY by `DeckEditor`. The print layer, the presentation overlay and the off-screen
// thumbnail capturer render `<Slide>` outside it and get the inert default, so a read-only
// copy of a slide can never publish an api — the same trick `DiagramEditingProvider` and
// `ThumbnailScheduleProvider` use.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { IApi } from "@svar-ui/react-gantt";

// Type-only import: erased at build time, so this file does NOT pull the gantt chunk into the
// main bundle (§7.1). Verify from the built asset list, not from intent.

export interface GanttEditingValue {
  // The ganttDocId the panel edits — the one the user is WORKING IN. Null until they point at
  // one, which is the whole of §8.6: see the note above `register`.
  activeDocId: string | null;
  api: IApi | null;
  // Called by `GanttBlock` when its widget initialises (api) and when it unmounts (null).
  //
  // §26/§8.6 **Registering is not selecting, and that distinction is the point.** This used to
  // be `publish`, which did both: the widget announced its api on mount and thereby became
  // "the active gantt". The editor mounts every slide of the deck at once, so what that
  // actually meant was "whichever gantt initialised last, anywhere in the deck" — invisible
  // while the panel was the only consumer, and wrong the moment the rail started asking
  // whether there is a gantt to edit, because the answer was yes from the first render of any
  // deck that contained one. A registered gantt is reachable; only a selected one is active.
  register: (docId: string, api: IApi | null) => void;
  // The selection event, from the two places that can produce one: a pointer down anywhere in
  // the block, and SVAR's own `select-task` (which also covers keyboard and programmatic
  // selection, the cases a pointer handler silently misses). Claiming a gantt that never
  // registered an api is a no-op — there would be nothing for the panel to drive.
  claim: (docId: string) => void;
  // §26/§8.5 "Show me this task" — the widget's own `show-editor`, which is what a DOUBLE
  // CLICK on a bar or a grid row emits. The task editor it wants to show lives in our right
  // panel, so the panel has to open or the double click does nothing visible: the selection
  // changes underneath a pane the user cannot see. `DiagramBlock` has had this since §21 via
  // `onEnterEdit`; this is the same wire for the same gesture.
  //
  // Deliberately a request, not a command: the provider's owner decides what opening means
  // (and gets to ask about an unsaved stylesheet draft first).
  requestEdit: () => void;
}

const INERT: GanttEditingValue = {
  activeDocId: null,
  api: null,
  register: () => {},
  claim: () => {},
  requestEdit: () => {},
};

const GanttEditingContext = createContext<GanttEditingValue>(INERT);

export function GanttEditingProvider({
  onEnterEdit,
  children,
}: {
  // Run when a gantt asks for its editor (a double click inside it). `DeckEditor` opens the
  // Gantt panel here — the same thing `DiagramEditingProvider`'s `onEnterEdit` does.
  onEnterEdit?: () => void;
  children: ReactNode;
}) {
  // Every mounted gantt's api, keyed by docId, and which one of them is selected. Two fields
  // rather than one because they answer different questions: `apis` is "what exists", which
  // mounting decides, and `activeDocId` is "what the user is in", which only a selection
  // decides. Collapsing them is what §8.6 had to undo.
  const [apis, setApis] = useState<Record<string, IApi>>({});
  const [activeDocId, setActiveDocId] = useState<string | null>(null);

  const register = useCallback((docId: string, api: IApi | null) => {
    setApis((prev) => {
      if (api === null) {
        if (!(docId in prev)) return prev;
        const next = { ...prev };
        delete next[docId];
        return next;
      }
      return prev[docId] === api ? prev : { ...prev, [docId]: api };
    });
    // An unmount gives the panel up only if the unmounting gantt is the one holding it. Two
    // gantts on a two-window slide mount and unmount in an order React chooses, and a bare
    // clear would let one block's teardown wipe the other's selection.
    if (api === null) {
      setActiveDocId((prev) => (prev === docId ? null : prev));
    }
  }, []);

  const claim = useCallback((docId: string) => {
    setActiveDocId((prev) => (prev === docId ? prev : docId));
  }, []);

  // Through a ref so `requestEdit` — and with it the whole context value — does not change
  // identity every time the callback is re-created by the parent's render. A context value
  // that changes per render re-renders every block on every slide, which is the cost §21 paid
  // once already (see the scene-tick note in `diagramEditing.tsx`).
  const enterEditRef = useRef(onEnterEdit);
  enterEditRef.current = onEnterEdit;
  const requestEdit = useCallback(() => enterEditRef.current?.(), []);

  // The selected gantt's api, or null — including when the selected one has since unmounted
  // and something cleared `apis` before `activeDocId`. Derived rather than stored so the two
  // can never disagree.
  const api = activeDocId ? (apis[activeDocId] ?? null) : null;

  const value = useMemo<GanttEditingValue>(
    () => ({ activeDocId, api, register, claim, requestEdit }),
    [activeDocId, api, register, claim, requestEdit],
  );

  return (
    <GanttEditingContext.Provider value={value}>
      {children}
    </GanttEditingContext.Provider>
  );
}

export function useGanttEditing(): GanttEditingValue {
  return useContext(GanttEditingContext);
}
